"use client";
import { fork, scopeBind } from "effector";
import { Provider } from "effector-react";
import { TooltipProvider } from "@components/organisms/assistant/ui/tooltip";
import { MotionProvider } from "@components/atoms/motion";
import React, { Suspense, useEffect, useMemo } from "react";
import { useParams, usePathname, useRouter, useSearchParams } from "next/navigation";
import { navigationPendingFinished, routerInit } from "@features/shared/router";
import { installScrollTrace } from "@features/debug/scrollTrace";
import {
    appLanguageChanged,
    appMounted,
    routeParamsChanged,
    routePathNameChanged,
    routeStateChanged
} from "@features/app/model";
import { AnalyticsService } from "@features/shared/analytics";
import { useTrackDeviceData } from "@features/shared/useTrackDeviceData";
import GTGuard from "./GTGuard";
import { useViewportHeight } from "@features/hooks/useViewportHeight";
import { UAParser } from "ua-parser-js";
import { setUA } from "@features/launch-context/model";
import i18n, { applyDetectedLanguage } from "../i18n";
import { contentLocaleChanged, getContentLocale, toContentLocale } from "@features/shared/locale";

const scope = fork();
/*attachLogger({ scope });

if (typeof window !== "undefined") {
    window.scope = scope;
}*/

function ProvidersContent({ children }: { children: React.ReactNode }) {
    const router = useRouter();
    const pathname = usePathname();
    const params = useParams();
    const searchParams = useSearchParams();
    const searchParamsString = useMemo(() => searchParams?.toString() ?? "", [searchParams]);

    const parser = new UAParser();
    const ua = parser.getResult();

    useViewportHeight();
    useTrackDeviceData();

    scopeBind(routerInit, { scope })(router);

    useEffect(() => {
        scopeBind(appMounted, { scope })();
    }, []);

    useEffect(() => {
        applyDetectedLanguage();
    }, []);

    // Notify the models that hold localized content so they refetch it with the
    // new locale instead of keeping the previously fetched language on screen.
    useEffect(() => {
        const emitContentLocale = scopeBind(contentLocaleChanged, { scope });
        const emitAppLanguage = scopeBind(appLanguageChanged, { scope });
        // The detected language was applied by the effect above, before this listener existed.
        emitAppLanguage(i18n.language);
        // Starts from the persisted locale, so the initial detection (which fires
        // languageChanged on mount) does not count as a change.
        let currentLocale = getContentLocale();

        const handleLanguageChanged = (lng: string) => {
            emitAppLanguage(lng);
            // Keep the analytics `language` property in sync with the UI language
            // (initial detection on mount and later picker changes alike).
            AnalyticsService.setLanguage(lng);
            const nextLocale = toContentLocale(lng);
            if (nextLocale === currentLocale) {
                return;
            }
            currentLocale = nextLocale;
            emitContentLocale(nextLocale);
        };

        i18n.on("languageChanged", handleLanguageChanged);
        return () => {
            i18n.off("languageChanged", handleLanguageChanged);
        };
    }, []);

    useEffect(() => {
        const setUABound = scopeBind(setUA, { scope });
        setUABound(ua);
    }, [ua]);

    useEffect(() => {
        const emitState = scopeBind(routeStateChanged, { scope });
        const finishNavigation = scopeBind(navigationPendingFinished, { scope });

        const normalizedParams = (params || {}) as Record<string, string | string[]>;
        emitState({ path: pathname, params: normalizedParams, search: searchParamsString });
        finishNavigation();
    }, [pathname, params, searchParamsString]);

    useEffect(() => {
        const routeChanged = scopeBind(routePathNameChanged, { scope });
        routeChanged(pathname);
    }, [pathname]);

    useEffect(() => {
        const fn = scopeBind(routeParamsChanged, { scope });
        fn((params || {}) as Record<string, string | string[]>);
    }, [params]);

    useEffect(() => {
        AnalyticsService.init();
    }, []);

    // QA aid: `?scrolltrace=1` records everything that moves the page (see features/debug/scrollTrace).
    useEffect(() => {
        installScrollTrace();
    }, []);

    return (
        <Provider value={scope}>
            <MotionProvider>
                <TooltipProvider>
                    <GTGuard />
                    {children}
                </TooltipProvider>
            </MotionProvider>
        </Provider>
    );
}

export function Providers({ children }: { children: React.ReactNode }) {
    return (
        <Suspense fallback={null}>
            <ProvidersContent>{children}</ProvidersContent>
        </Suspense>
    );
}
